import cx from "clsx";
import { User } from "kreate-common/modules/business-types";
import ButtonSecondary from "kreate-common/modules/kreate-ui/components/ButtonSecondary";
import DropdownMenu from "kreate-common/modules/kreate-ui/components/DropdownMenu";
import Flex from "kreate-common/modules/kreate-ui/components/Flex";
import { httpPost$LoveUser } from "kreate-server/commands/LoveUser/fetcher";
import { useRouter } from "next/router";
import * as React from "react";
import { FaCrown, FaFacebook } from "react-icons/fa";
import { FaXTwitter } from "react-icons/fa6";
import {
  MdAdd,
  MdAddPhotoAlternate,
  MdEdit,
  MdFavorite,
  MdFavoriteBorder,
  MdLink,
  MdOutlineArrowDropDown,
  MdPhotoSizeSelectActual,
  MdShare,
} from "react-icons/md";

import runFlow$UpdateVerification from "../../containers/SectionHead/generators/runFlow$UpdateVerification";

import styles from "./index.module.scss";

import { useLoginStatus } from "@/modules/common-hooks/useLoginStatus";
import { shamelesslyRevalidateEverything } from "@/modules/common-utils/shamelesslyRevalidateEverything";
import {
  copyUrlToClipboard,
  shareOnFacebook,
  shareOnTwitter,
} from "@/modules/common-utils/shareOn";
import IconVerified from "@/modules/kreate-components/components/UserDisplayNameViewerV2/icons/IconVerified";
import { useToast } from "@/modules/kreate-contexts/ToastContext";
import { useModalTrigger$ModalAsyncGenerator } from "@/modules/kreate-modals/modals/ModalAsyncGenerator/hooks";
import {
  getLinkToPageProfile,
  getLinkToPageRoyalties,
} from "@/modules/navigation/utils/getLinkTo";

type Props = {
  className?: string;
  style?: React.CSSProperties;
  user: User | null | undefined;
};

export default function Toolbar({ className, style, user }: Props) {
  const { showMessage, showError } = useToast();
  const router = useRouter();
  const loginStatus = useLoginStatus();

  const modalTriggers = {
    ModalAsyncGenerator: useModalTrigger$ModalAsyncGenerator(),
  };

  const isOwner = loginStatus?.loggedIn && loginStatus.userId === user?.userId;
  const isLoved =
    loginStatus?.loggedIn && user?.lovedBy?.includes(loginStatus.userId);

  return (
    <div className={cx(styles.container, className)} style={style}>
      <Flex.Row
        flex="0 0 auto"
        alignItems="flex-start"
        gap="12px"
        flexWrap="wrap"
        justifyContent="flex-end"
      >
        {loginStatus?.loggedIn ? (
          <ButtonSecondary
            content="Love"
            icon={
              isLoved ? (
                <MdFavorite className={styles.colorFgPrimary} />
              ) : (
                <MdFavoriteBorder />
              )
            }
            onClick={async () => {
              try {
                await httpPost$LoveUser(`/api/v1/user/${user?.userId}/love`, {
                  love: !isLoved,
                });
                await shamelesslyRevalidateEverything();
              } catch (error) {
                showError({ error });
              }
            }}
          />
        ) : null}

        {loginStatus?.loggedIn && loginStatus.isAgent ? (
          <>
            <ButtonSecondary
              content="Create Curated Collection"
              onClick={() => router.push("/new/curated-collection")}
            />
            <ButtonSecondary
              icon={<IconVerified />}
              content={user?.isVerified ? "Unverify" : "Verify"}
              onClick={
                !user
                  ? undefined
                  : async () => {
                      try {
                        await modalTriggers.ModalAsyncGenerator.openModal(() =>
                          runFlow$UpdateVerification({
                            userId: user.userId,
                            newVerified: !user.isVerified,
                          })
                        );
                      } catch (error) {
                        showError({ error });
                      }
                    }
              }
            />
          </>
        ) : (
          <div></div>
        )}
        {isOwner ? (
          <DropdownMenu.Menu
            trigger={
              <ButtonSecondary
                icon={<MdAdd style={{ marginLeft: "-5px" }} />}
                iconRight={
                  <MdOutlineArrowDropDown
                    size="24px"
                    style={{ margin: "0 -6px" }}
                  />
                }
                content="Add"
                disabled={!loginStatus?.confirmed}
              />
            }
          >
            <DropdownMenu.Item
              icon={<MdAddPhotoAlternate />}
              content="Artwork"
              onSelect={async () => await router.push("/new/artwork")}
            />
            <DropdownMenu.Item
              icon={<MdPhotoSizeSelectActual />}
              content="Collection"
              onSelect={async () => await router.push("/new/collection")}
            />
          </DropdownMenu.Menu>
        ) : null}
        {isOwner ? (
          <ButtonSecondary
            content="Edit Profile"
            icon={<MdEdit />}
            onClick={async () => await router.push("/me/edit")}
          />
        ) : null}
        {isOwner ? (
          <ButtonSecondary
            icon={<FaCrown />}
            content="Set Royalties"
            onClick={async () =>
              await router.push(
                getLinkToPageRoyalties({ collectionId: undefined })
              )
            }
          />
        ) : undefined}
        <DropdownMenu.Menu
          trigger={
            <ButtonSecondary
              size="md-square"
              padding="minimal"
              icon={<MdShare />}
              title="Share"
            />
          }
        >
          <DropdownMenu.Item
            icon={<FaXTwitter />}
            content={"Twitter"}
            disabled={!user}
            onSelect={() => user && shareOnTwitter(getLinkToPageProfile(user))}
          />
          <DropdownMenu.Item
            icon={<FaFacebook />}
            content={"Facebook"}
            disabled={!user}
            onSelect={() => user && shareOnFacebook(getLinkToPageProfile(user))}
          />
          <DropdownMenu.Item
            icon={<MdLink />}
            content={"Copy Link"}
            disabled={!user}
            onSelect={() => {
              if (!user) return;
              copyUrlToClipboard(getLinkToPageProfile(user));
              showMessage({ color: "success", title: "Copied to Clipboard" });
            }}
          />
        </DropdownMenu.Menu>
      </Flex.Row>
    </div>
  );
}
